import { useListContext, useLocaleState, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { formatRelativeDate } from "../misc/RelativeDate";
import type { Company } from "../types";
import { applyDefaultTieBreak } from "./accountsSort";

/**
 * The accounts list as a one-column card list, for the mobile home screen.
 *
 * Same records, same order and same numbers as the desktop table — a phone just
 * cannot carry seven columns, so each account becomes a card carrying only what
 * triage needs: who it is, how much is open and late, what is next, and how
 * long since anyone touched it.
 */
export const AccountsCardList = () => {
  const translate = useTranslate();
  const { data, sort, isPending } = useListContext<Company>();

  if (isPending) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (!data?.length) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("crm.dashboard.accounts.empty")}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {applyDefaultTieBreak(data, sort).map((company) => (
        <li key={company.id}>
          <AccountCard company={company} />
        </li>
      ))}
    </ul>
  );
};

const AccountCard = ({ company }: { company: Company }) => {
  const translate = useTranslate();
  const [locale = "en"] = useLocaleState();
  const overdueCount = company.nb_overdue_tasks ?? 0;

  return (
    <Card className="p-3 gap-2">
      <Link
        to={`/companies/${company.id}/show`}
        className="flex items-center gap-2 font-medium hover:underline"
      >
        <CompanyAvatar record={company} width={20} height={20} />
        <span className="truncate">{company.name}</span>
      </Link>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <AccountFact label={translate("crm.dashboard.accounts.col_open")}>
          <span className="tabular-nums">{company.nb_open_tasks ?? 0}</span>
          {overdueCount > 0 && (
            <span className="ml-1 text-destructive tabular-nums">
              {`· ${translate("crm.dashboard.accounts.overdue_suffix", {
                count: overdueCount,
              })}`}
            </span>
          )}
        </AccountFact>

        <AccountFact label={translate("crm.dashboard.accounts.col_last_touch")}>
          {company.last_activity_at
            ? formatRelativeDate(company.last_activity_at, locale)
            : "—"}
        </AccountFact>

        <AccountFact label={translate("crm.dashboard.accounts.col_next")}>
          {company.next_task_due
            ? formatRelativeDate(company.next_task_due, locale)
            : translate("crm.dashboard.accounts.none")}
        </AccountFact>
      </dl>
    </Card>
  );
};

const AccountFact = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="text-foreground">{children}</dd>
  </div>
);
